import { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { useAtom } from 'jotai';
import * as yup from 'yup';

import { createSupportPlan } from '@/api/createSupportPlan';
import { getChild } from '@/api/getChild';
import { getSupportPlanDraft } from '@/api/getSupportPlanDraft';
import Breadcrumbs, { BreadcrumbItem } from '@/components/Breadcrumbs/Breadcrumbs';
import { FormContainer } from '@/components/supportPlans/FormContainer';
import { UserAtom, NotificationAtom } from '@/jotai/jotai';
import {
  validCategoryValues,
  validFiveAreaValues,
  validAchievementPeriodValues,
} from '@/logics/supportPlan/supportPlan';
import { Child } from '@/types/child';
import {
  SupportPlanRequest,
  DayOfWeek,
  SupportPlanItem,
  SupportPlanSchedule,
  SupportPlanDraft,
} from '@/types/supportPlan';

interface SupportPlanFormData {
  created_date: Date; // フォームではDate型として扱う
  child_family_intentions?: string;
  general_policy?: string;
  long_term_goal?: string;
  short_term_goal?: string;
  support_schedule_details?: string;
  extension_reason?: string;
  special_notes?: string;
  items?: SupportPlanItem[];
  schedules?: {
    [key: string]: {
      service_start_time?: string;
      service_end_time?: string;
      pre_extension_start_time?: string;
      pre_extension_end_time?: string;
      post_extension_start_time?: string;
      post_extension_end_time?: string;
    };
  };
}

export const CreateSupportPlanPage = () => {
  const [_, setNotification] = useAtom(NotificationAtom);
  const [loginUser] = useAtom(UserAtom);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftID = searchParams.get('draftID');
  const childIDParam = searchParams.get('childID');
  const supportPlanDraftID = parseInt(draftID || '0', 10);
  const childID = parseInt(childIDParam || '0', 10);

  const { pathname: pathName } = useLocation();

  const [child, setChild] = useState<Child>();
  const [supportPlanDraft, setSupportPlanDraft] = useState<SupportPlanDraft>();

  useEffect(() => {
    const fetchChild = async () => {
      try {
        const result = await getChild(childID);
        if (result.status !== 200) {
          setNotification({
            headingLevel: 'h2',
            type: 'error',
            bannerStyle: 'standard',
            title: '利用者情報の取得に失敗しました',
            message: '利用者情報の取得に失敗しました',
          });
          throw new Error('Failed to fetch child data');
        }
        setChild(result.result);
      } catch (error) {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '利用者情報の取得に失敗しました',
          message: '利用者情報の取得に失敗しました',
        });
        console.error('Error fetching child data:', error);
      }
    };

    const fetchSupportPlanDraft = async () => {
      if (supportPlanDraftID && supportPlanDraftID > 0) {
        try {
          const result = await getSupportPlanDraft(supportPlanDraftID);
          if (result.status !== 200) {
            setNotification({
              headingLevel: 'h2',
              type: 'error',
              bannerStyle: 'standard',
              title: '個別支援計画（案）の取得に失敗しました',
              message: '個別支援計画（案）の取得に失敗しました',
            });
            throw new Error('Failed to fetch support plan draft data');
          }
          setSupportPlanDraft(result.result);
        } catch (error) {
          setNotification({
            headingLevel: 'h2',
            type: 'error',
            bannerStyle: 'standard',
            title: '個別支援計画（案）の取得に失敗しました',
            message: '個別支援計画（案）の取得に失敗しました',
          });
          console.error('Error fetching support plan draft data:', error);
        }
      }
    };

    fetchChild();
    fetchSupportPlanDraft();
  }, [childID, supportPlanDraftID]);

  const breadcrumbs: BreadcrumbItem[] = [{ path: pathName, name: '個別支援計画登録' }];

  const supportPlanItemSchema = yup.object({
    category: yup
      .string()
      .oneOf(validCategoryValues, 'カテゴリーは有効な値でなければなりません')
      .required('カテゴリーは必須です'),
    support_goal: yup.string().optional(),
    support_content: yup.string().optional(),
    five_area: yup
      .string()
      .oneOf(validFiveAreaValues, '5エリアは有効な値でなければなりません')
      .optional(),
    achievement_period: yup
      .string()
      .oneOf(validAchievementPeriodValues, '達成期間は有効な値でなければなりません')
      .required('達成期間は必須です'),
    support_provider: yup.string().optional(),
    notes: yup.string().optional(),
    priority: yup
      .number()
      .required('優先度は必須です')
      .positive('優先度は正の数でなければなりません'),
  });

  const timeScheduleSchema = yup.object({
    service_start_time: yup.string().optional(),
    service_end_time: yup.string().optional(),
    pre_extension_start_time: yup.string().optional(),
    pre_extension_end_time: yup.string().optional(),
    post_extension_start_time: yup.string().optional(),
    post_extension_end_time: yup.string().optional(),
  });

  const supportPlanRequestSchema = yup.object({
    created_date: yup.date().required('作成日は必須です'),
    child_family_intentions: yup.string().optional(),
    general_policy: yup.string().optional(),
    long_term_goal: yup.string().optional(),
    short_term_goal: yup.string().optional(),
    support_schedule_details: yup.string().optional(),
    extension_reason: yup.string().optional(),
    special_notes: yup.string().optional(),
    items: yup.array().of(supportPlanItemSchema).optional(),
    schedules: yup
      .object()
      .shape({
        monday: timeScheduleSchema.optional(),
        tuesday: timeScheduleSchema.optional(),
        wednesday: timeScheduleSchema.optional(),
        thursday: timeScheduleSchema.optional(),
        friday: timeScheduleSchema.optional(),
        saturday: timeScheduleSchema.optional(),
        sunday_holiday: timeScheduleSchema.optional(),
      })
      .optional(),
  });
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { isValid, errors },
  } = useForm<SupportPlanFormData>({
    resolver: yupResolver(supportPlanRequestSchema),
    mode: 'onChange',
    defaultValues: supportPlanDraft
      ? convertSupportPlanDraftToFormData(supportPlanDraft)
      : undefined,
  });

  // 支援計画（案）データが取得できたらフォームの初期値を設定
  useEffect(() => {
    if (supportPlanDraft) {
      reset(convertSupportPlanDraftToFormData(supportPlanDraft));
    }
  }, [supportPlanDraft, reset]);

  const onSubmit: SubmitHandler<SupportPlanFormData> = async (data) => {
    console.log(data);
    const input: SupportPlanRequest = {
      support_plan_draft_id: supportPlanDraftID,
      office_id: 1,
      child_id: childID,
      created_date: data.created_date.toISOString(),
      email: loginUser?.email || '',
      role: loginUser?.role || '',
      status: 'in_progress', // デフォルト値を設定
      child_family_intentions: data.child_family_intentions,
      general_policy: data.general_policy,
      long_term_goal: data.long_term_goal,
      short_term_goal: data.short_term_goal,
      support_schedule_details: data.support_schedule_details,
      extension_reason: data.extension_reason,
      special_notes: data.special_notes,
      items: data.items,
    };

    if (data.schedules) {
      const schedulesArray = Object.entries(data.schedules)
        .filter(([_, timeData]) => {
          if (!timeData || Object.keys(timeData).length === 0) return false;
          return true;
        })
        .map(([day, timeData]) => {
          return {
            day_of_week: day as DayOfWeek,
            service_start_time: timeData.service_start_time,
            service_end_time: timeData.service_end_time,
            pre_extension_start_time: timeData.pre_extension_start_time,
            pre_extension_end_time: timeData.pre_extension_end_time,
            post_extension_start_time: timeData.post_extension_start_time,
            post_extension_end_time: timeData.post_extension_end_time,
          };
        });

      input.schedules = schedulesArray as SupportPlanSchedule[];
    }

    console.log(input);
    try {
      const result = await createSupportPlan(input);
      console.log(result);
      if (result.status === 201) {
        setNotification({
          headingLevel: 'h2',
          type: 'success',
          bannerStyle: 'standard',
          title: '個別支援計画を保存しました',
          message: '個別支援計画を保存しました',
        });
        console.log(result.result);
        // 保存成功後に利用者の個別支援計画一覧画面へ遷移
        navigate(`/admin/children/${childID}/support-plans`);
      } else {
        console.log('保存に失敗');
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '個別支援計画の保存に失敗しました',
          message: '個別支援計画の保存に失敗しました',
        });
        console.log('error');
      }
    } catch (error) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: '個別支援計画の保存に失敗しました',
        message: '個別支援計画の保存に失敗しました',
      });
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-row">
        <Breadcrumbs item={breadcrumbs} />
      </div>
      <FormContainer
        register={register}
        control={control}
        errors={errors}
        isValid={isValid}
        onSubmit={handleSubmit(onSubmit)}
        onCancel={() => navigate(-1)}
        childName={(child?.last_name || '') + (child?.first_name || '')}
        creatorName={(loginUser?.last_name || '') + (loginUser?.first_name || '')}
        isEdit={false}
      />
    </div>
  );
};

// 支援計画（案）のデータを個別支援計画フォームデータに変換する関数
function convertSupportPlanDraftToFormData(draft: SupportPlanDraft): SupportPlanFormData {
  const convertedSchedules: SupportPlanFormData['schedules'] = {};

  if (draft.schedules && draft.schedules.length > 0) {
    draft.schedules.forEach((schedule) => {
      convertedSchedules[schedule.day_of_week] = {
        service_start_time: schedule.service_start_time,
        service_end_time: schedule.service_end_time,
        pre_extension_start_time: schedule.pre_extension_start_time,
        pre_extension_end_time: schedule.pre_extension_end_time,
        post_extension_start_time: schedule.post_extension_start_time,
        post_extension_end_time: schedule.post_extension_end_time,
      };
    });
  }

  return {
    created_date: new Date(draft.created_date), // 文字列からDate型に変換
    child_family_intentions: draft.child_family_intentions,
    general_policy: draft.general_policy,
    long_term_goal: draft.long_term_goal,
    short_term_goal: draft.short_term_goal,
    support_schedule_details: draft.support_schedule_details,
    extension_reason: draft.extension_reason,
    special_notes: draft.special_notes,
    items: draft.items,
    schedules: Object.keys(convertedSchedules).length > 0 ? convertedSchedules : undefined,
  };
}
